import React, { useState } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { useCart } from "../../context/cart-context";
import { useWishlist } from "../../context/wishlist-context";

const BRAND = "#194049";

export const ProductCard = ({ id, imgSrc, title, path, price, slug, limit }) => {
  const { cart, addToCart } = useCart();
  const { isWishlisted, toggle } = useWishlist();
  const [qty, setQty] = useState(1);

  const inCart = cart.some((item) => item.id === id);
  const wishlisted = isWishlisted(id);

  const rawPrice =
    typeof price === "number"
      ? price
      : parseFloat(String(price).replace(/[^0-9.]/g, "")) || 0;

  const handleAddToCart = () => {
    if (!inCart) addToCart(id, imgSrc, title, slug, rawPrice, qty, limit || 99);
  };

  const handleWishlist = () => {
    toggle({ id, imgSrc, title, path, price: rawPrice, slug });
  };

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "12px",
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: "0 6px 20px rgba(0,0,0,0.09)" },
      }}
    >
      {/* Image */}
      <Box
        component={Link}
        to={path}
        sx={{ display: "block", bgcolor: "#f9fafb", aspectRatio: "1/1", overflow: "hidden", textDecoration: "none" }}
      >
        <Box
          component="img"
          src={imgSrc}
          alt={title}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            mixBlendMode: "multiply",
            transition: "transform 0.45s ease",
            "&:hover": { transform: "scale(1.04)" },
          }}
        />
      </Box>

      <Divider />

      {/* Zoom + Wishlist icon row */}
      <Box sx={{ display: "flex", justifyContent: "space-between", px: 1.5, py: 0.75 }}>
        <IconButton
          component={Link}
          to={path}
          size="small"
          title="View product"
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: "50%",
            p: 0.75,
            color: "#9ca3af",
            "&:hover": { color: BRAND, borderColor: BRAND, bgcolor: "transparent" },
          }}
        >
          <ZoomInIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <IconButton
          size="small"
          onClick={handleWishlist}
          title={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
          sx={{
            border: "1px solid",
            borderRadius: "50%",
            p: 0.75,
            color: wishlisted ? "#ef4444" : "#9ca3af",
            borderColor: wishlisted ? "#fecaca" : "#e5e7eb",
            "&:hover": { color: "#ef4444", borderColor: "#fecaca", bgcolor: "transparent" },
          }}
        >
          {wishlisted ? <FavoriteIcon sx={{ fontSize: 18 }} /> : <FavoriteBorderIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>

      <Divider />

      {/* Product info */}
      <Box sx={{ px: 2, pt: 1.75, pb: 2, display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
        {/* Name */}
        <Link to={path} style={{ textDecoration: "none" }}>
          <Typography
            sx={{
              fontSize: { xs: 13, md: 14 },
              fontWeight: 600,
              color: "#111827",
              lineHeight: 1.35,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {title}
          </Typography>
        </Link>

        {/* Price */}
        <Typography sx={{ fontSize: { xs: 14, md: 15 }, fontWeight: 700, color: BRAND }}>
          £{rawPrice.toFixed(2)}
        </Typography>

        {/* Quantity stepper + Add to Cart */}
        <Box sx={{ display: "flex", gap: 1, alignItems: "stretch", mt: 0.5 }}>
          {/* Stepper */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <Box
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              sx={{
                px: { xs: 1, md: 1.25 },
                py: 0.875,
                cursor: "pointer",
                fontSize: 15,
                color: "#374151",
                userSelect: "none",
                "&:hover": { bgcolor: "#f3f4f6" },
              }}
            >
              –
            </Box>
            <Typography
              sx={{
                px: { xs: 1.25, md: 1.5 },
                fontSize: 13,
                fontWeight: 500,
                color: "#111827",
                borderLeft: "1px solid #e5e7eb",
                borderRight: "1px solid #e5e7eb",
                lineHeight: "30px",
              }}
            >
              {qty}
            </Typography>
            <Box
              onClick={() => setQty((q) => Math.min(limit || 99, q + 1))}
              sx={{
                px: { xs: 1, md: 1.25 },
                py: 0.875,
                cursor: "pointer",
                fontSize: 15,
                color: "#374151",
                userSelect: "none",
                "&:hover": { bgcolor: "#f3f4f6" },
              }}
            >
              +
            </Box>
          </Box>

          {/* Add to Cart */}
          <Box
            onClick={handleAddToCart}
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              bgcolor: inCart ? "#6b7280" : "#111827",
              color: "#fff",
              borderRadius: "6px",
              fontSize: { xs: 10, md: 11 },
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              cursor: inCart ? "default" : "pointer",
              py: 0.875,
              px: 1,
              transition: "background 0.2s",
              "&:hover": { bgcolor: inCart ? "#6b7280" : BRAND },
              whiteSpace: "nowrap",
            }}
          >
            <ShoppingCartOutlinedIcon sx={{ fontSize: 14 }} />
            {inCart ? "In Cart" : "Add to Cart"}
          </Box>
        </Box>

        {/* View Details link */}
        <Box sx={{ mt: 0.25 }}>
          <Link to={path} style={{ textDecoration: "none" }}>
            <Typography
              sx={{
                fontSize: 12,
                color: "#9ca3af",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                "&:hover": { color: BRAND },
                transition: "color 0.15s",
              }}
            >
              View Details
            </Typography>
          </Link>
        </Box>
      </Box>
    </Box>
  );
};
